import type { ChangeEvent, ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { type ProductListArticlesSortOption as SortOption } from 'graphql-ts-types';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  FlexProps,
  HeightProps,
  JustifyContentProps,
  LeftProps,
  PaddingLeftProps,
  PaddingProps,
  PaddingRightProps,
  PositionProps,
  SizeProps,
  TopProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  height,
  justifyContent,
  left,
  paddingLeft,
  paddingRight,
  position,
  size,
  space,
  themeGet,
  top,
  width,
} from 'styled-system';

import Icon from 'common/components/Icon';
import { BorderlessSelect } from 'common/components/Select';
import { HeadlineH8, SubtitleST3 } from 'common/components/Typography';
import { useMatchesViewport } from 'common/hooks/media';
import { useLocalization } from 'common/providers/LocaleProvider';
import { useRouter } from 'common/providers/RouterProvider';
import type { BoxSizingProps, CursorProps, PointerEventProps } from 'common/theme/system-utilities';
import { boxSizing, cursor, pointerEvents } from 'common/theme/system-utilities';
import { trackChangingSortOption, trackOpenSortOptions } from 'common/tracking/filters';
import type { SortParams as CategorySortOptionsProps } from 'common/types/SortAndFilter';
import { getProductsListPath, getUrlWithQueryParams } from 'common/utils/url';
import { useOptionButtons } from '../../category-options-buttons/components/hooks';

const Label = styled(SubtitleST3)<TopProps & LeftProps & PositionProps & PointerEventProps>`
  ${left};
  ${pointerEvents};
  ${position};
  ${top};
`;

const SortHitArea = styled.div<PaddingProps & BackgroundColorProps & CursorProps>`
  ${space};
  ${cursor};
`;

const sortHitAreaStyles: ComponentProps<typeof SortHitArea> = {
  padding: ['sp_4', 'sp_4', null],
  paddingLeft: 'sp_0',
  cursor: 'pointer',
};

const labelStyles: ComponentProps<typeof Label> = {
  as: 'label',
  color: 'sys.neutral.text.default',
  top: ['0px', null, '9px', '12px'],
  left: '12px',
  position: 'absolute',
  pointerEvents: 'none',
  fontWeight: 'normal',
};

const Sort = styled(HeadlineH8)<
  DisplayProps &
    AlignItemsProps &
    FlexProps &
    BorderWidthProps &
    BorderRadiusProps &
    BorderColorProps &
    BorderStyleProps &
    BackgroundColorProps &
    JustifyContentProps &
    CursorProps &
    SizeProps &
    HeightProps &
    WidthProps &
    BorderProps &
    PaddingLeftProps &
    PaddingRightProps &
    BoxSizingProps
>`
  ${display};
  ${alignItems};
  ${justifyContent};
  ${backgroundColor};
  ${borderWidth};
  ${borderRadius};
  ${borderColor};
  ${borderStyle};
  ${cursor};
  ${size};
  ${height};
  ${width};
  ${border};
  ${paddingLeft};
  ${paddingRight};
  ${boxSizing};

  // Override default button spacing
  padding-block: 0;

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
    outline: 1px solid ${themeGet('colors.sys.neutral.border.hover')};
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
  }

  &:focus-within {
    background-color: ${themeGet('colors.sys.neutral.background.default')};
    outline: 2px solid ${themeGet('colors.sys.secondary.border.default')};
    border-color: ${themeGet('colors.sys.neutral.border.default')};
    outline-offset: 2px;
  }
`;

const sortDropdownStyles: ComponentProps<typeof Sort> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'sys.neutral.background.default',
  borderColor: 'sys.neutral.border.default',
  borderRadius: '100px',
  borderStyle: 'solid',
  borderWidth: 'small',
  height: [40, null, null, 48],
  width: [40, null, 'auto', null],
  cursor: 'pointer',
  paddingLeft: ['sp_2', null, 'sp_0'],
  paddingRight: 'sp_2',
};

const SortIcon = styled(Icon)<any>`
  ${size};
`;

const sortIconStyles: ComponentProps<typeof SortIcon> = {
  name: 'sort',
  size: 24,
};

const SortContainer = styled.div<DisplayProps & PositionProps & WidthProps>`
  ${display};
  ${position};
  ${position};
  ${width};
`;

const sortContainerStyles: ComponentProps<typeof SortContainer> = {
  width: 'calc(100% - 4px)',
  position: 'relative',
  display: ['none', null, 'flex'],
};

const sortIconContainerStyles: ComponentProps<typeof SortContainer> = {
  display: ['flex', null, 'none'],
};

const withoutShopInShopStyles: ComponentProps<typeof Sort> = {
  boxSizing: 'border-box',
  height: [32, null, 40],
  width: [32, null, 'auto'],
};

const CategorySortOptions = ({
  showCategoriesAsFilter,
  order,
  sortOptions,
  withShopInShopNav,
  isSquare,
}: CategorySortOptionsProps) => {
  const {
    matches: { matchesDesktop },
  } = useMatchesViewport();

  const { t } = useLocalization();

  const { onSortButtonClick } = useOptionButtons(showCategoriesAsFilter);

  const router = useRouter();

  const selectOptionHandler = ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
    trackChangingSortOption({
      sortOptions,
      selectedOrder: value,
    });

    const url = getUrlWithQueryParams({
      order: value as SortOption,
      page: '',
    });
    const path = getProductsListPath(url);
    router.push(path, { scroll: true });
  };

  const selected = sortOptions.find(o => o.option === order);
  const ariaLabel = `${t('h24_sort')}: ${selected?.localized ?? order}`;

  return (
    <SortHitArea
      {...sortHitAreaStyles}
      onClick={onSortButtonClick}
      data-corgi-id="category-sort-options"
    >
      <Sort
        {...sortDropdownStyles}
        {...(!withShopInShopNav && withoutShopInShopStyles)}
        {...(isSquare && {
          borderRadius: 'medium',
          borderColor: 'sys.neutral.border.weak',
          height: '32px',
        })}
        data-testid="sort-options-dropdown"
        aria-hidden="true"
        as={matchesDesktop ? 'div' : 'button'}
      >
        <SortContainer
          {...sortContainerStyles}
          data-testid="sort-options-dropdown"
          aria-label={ariaLabel}
        >
          <Label aria-hidden="true" {...labelStyles}>
            {t('h24_sort_by')}:
          </Label>
          <BorderlessSelect
            name="borderless-select"
            optionsV2
            ariaLabel={t('sort_options_title')}
            title={t('sort_options_title')}
            value={order}
            options={sortOptions}
            onSelect={selectOptionHandler}
            onClick={() => trackOpenSortOptions()}
          />
        </SortContainer>
        <SortContainer
          {...sortIconContainerStyles}
          data-testid="sort-options-button"
          aria-label={ariaLabel}
        >
          <SortIcon {...sortIconStyles}>
            <Icon name="sort" />
          </SortIcon>
        </SortContainer>
      </Sort>
    </SortHitArea>
  );
};

export default CategorySortOptions;
